import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import PlayerName from "@/components/PlayerName";
import { toast } from "sonner";
import { Send, Trash2 } from "lucide-react";

export default function PostComments({ postId, onCountChange }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/community/posts/${postId}/comments`).then(({ data }) => setComments(data));
  }, [postId]);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/community/posts/${postId}/comments`, { content: text });
      setComments((c) => [...c, data]);
      onCountChange?.(comments.length + 1);
      setText("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/community/comments/${c.id}`);
      const next = comments.filter((x) => x.id !== c.id);
      setComments(next);
      onCountChange?.(next.length);
      toast.success("Comment deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const canDelete = (c) => user?.role === "admin" || c.user_id === user?.id;

  return (
    <div className="mt-3 border-t border-pb-border/50 pt-3 space-y-3" data-testid={`comments-${postId}`}>
      {comments.map((c) => (
        <div key={c.id} className="flex gap-2.5 items-start group">
          <Avatar src={c.avatar} name={c.author} size={28} />
          <div className="bg-pb-elevated border border-pb-border/60 px-3 py-2 flex-1 min-w-0">
            <PlayerName userId={c.user_id} name={c.author} className="text-xs font-semibold text-zinc-300" testId={`comment-author-${c.id}`} />
            <div className="text-sm text-zinc-200 whitespace-pre-wrap break-words">{c.content}</div>
          </div>
          {canDelete(c) && (
            <button data-testid={`comment-delete-${c.id}`} onClick={() => remove(c)} title="Delete comment"
              className="text-zinc-700 hover:text-red-400 transition-colors mt-2 shrink-0">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      <div className="flex gap-2">
        <input data-testid={`comment-input-${postId}`} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder="Write a comment…"
          className="flex-1 bg-pb-elevated border border-pb-border px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
        <button data-testid={`comment-submit-${postId}`} onClick={submit} disabled={busy || !text.trim()}
          className="bg-pb-red text-black px-3 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40">
          <Send className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
